import { useCallback, useEffect, useState } from 'react';
import { usePrefersDark } from './useMediaQuery';

export type ThemePreference = 'light' | 'dark' | 'system';
export type TextScale = 'normal' | 'large';

const THEME_KEY = 'flos:theme';
const SCALE_KEY = 'flos:scale';

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    return allowed.includes(raw as T) ? (raw as T) : fallback;
  } catch {
    // Modo privado do Safari lança em localStorage. Não é motivo para a tela cair.
    return fallback;
  }
}

function write(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Sem persistência, mas a sessão atual continua a funcionar.
  }
}

/**
 * Tema claro/escuro.
 *
 * `system` segue o Android; 'light'/'dark' são escolha explícita dela e ganham
 * sempre — os olhos dela decidem, não o sistema operativo. A escolha fica no
 * `data-theme` do <html>, que é o que os tokens leem.
 */
export function useTheme() {
  const prefersDark = usePrefersDark();
  const [preference, setPreference] = useState<ThemePreference>(() =>
    readStored(THEME_KEY, ['light', 'dark', 'system'] as const, 'system'),
  );

  const resolved: 'light' | 'dark' =
    preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (preference === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', preference);
    }
  }, [preference]);

  const setTheme = useCallback((next: ThemePreference) => {
    setPreference(next);
    write(THEME_KEY, next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setTheme]);

  return { theme: preference, resolved, setTheme, toggle };
}

/**
 * Tamanho da letra.
 *
 * `large` multiplica toda a escala por 1.25 (o corpo passa de 22 px a 27.5 px).
 * Isto é uma definição de produto a sério, não um extra: há dias em que 22 px
 * não chegam.
 */
export function useTextScale() {
  const [scale, setScaleState] = useState<TextScale>(() =>
    readStored(SCALE_KEY, ['normal', 'large'] as const, 'normal'),
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (scale === 'large') {
      root.setAttribute('data-scale', 'large');
    } else {
      root.removeAttribute('data-scale');
    }
  }, [scale]);

  const setScale = useCallback((next: TextScale) => {
    setScaleState(next);
    write(SCALE_KEY, next);
  }, []);

  return { scale, setScale, toggle: () => setScale(scale === 'large' ? 'normal' : 'large') };
}
